import React, { useEffect, useMemo, useState } from "react";
import MUITable from "Components/UI/Tables/MUITable";
import { Link } from "react-router-dom";
import IconButton from "@mui/material/IconButton";
import LinkIcon from "@mui/icons-material/Link";

import { price2StringPrice } from "utils/priceformatter";
import cutStrAtAndAddDots from "utils/cutStrAtAndAddDots";

import {
  getParseStatusLabelById,
  getSystemStatusLabelById,
} from "utils/labels";
import {
  getSystemStatusColorByLabel,
  getParseStatusColorByLabel,
} from "utils/colors";
import CommonChip from "Components/UI/Tables/CommonChip";
import { useEndCustomerPaid } from "Context/EndCustomerPaid";
import useTableOptions from "Hooks/useTableOptions";

const createTableRows = (invoices) => {
  return [
    ...invoices?.map((i) => {
      return [
        i.Id,
        i.Id,
        price2StringPrice(i.Amount ? i.Amount : 0),
        i.OCR,
        i.EndCustomerName,
        i.Address,
        i.PostalNr,
        i.City,
        i.PaymentDate,
        i.Comment,
        i.FileId,
        i.AccountRefId,
        i.InvoiceId,
        getParseStatusLabelById(i.ParseStatus),
        cutStrAtAndAddDots(i.ParseMessage, 45),
        getSystemStatusLabelById(i.SystemStatus),
      ];
    }),
  ];
};

export default function EndCustomerPaidsTable({ paidInvoices }) {
  const { invoices, fetchInvoices } = useEndCustomerPaid();
  const [rows, setRows] = useState([]);
  const { handleColumnShow, isColDisplayed } = useTableOptions(
    "EndCustomerPaidsTable"
  );

  useEffect(() => {
    if (!paidInvoices) {
      fetchInvoices();
    }
  }, []);

  useEffect(() => {
    const rows = createTableRows(paidInvoices ? paidInvoices : invoices);
    setRows(rows);
  }, [invoices, paidInvoices]);

  const columns = [
    {
      name: "",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => {
          return (
            <IconButton
              component={Link}
              to={`/endcustomerpaid/${value}`}
              aria-label="edit"
              size="small"
            >
              <LinkIcon sx={{ p: 0, m: 0 }} />
            </IconButton>
          );
        },
      },
    },
    {
      name: "Id",
      label: "ID",
      options: {
        filter: false,
        display: isColDisplayed("Id"),
      },
    },
    {
      name: "Amount",
      label: "Belopp",
      options: {
        display: isColDisplayed("Amount"),
        filter: false,
      },
    },
    {
      name: "OCR",
      label: "OCR",
      options: {
        display: isColDisplayed("OCR"),
        filter: false,
      },
    },
    {
      name: "EndCustomerName",
      label: "Kundsnamn",
      options: {
        display: isColDisplayed("EndCustomerName"),
        filter: false,
      },
    },
    {
      name: "Address",
      label: "Adress",
      options: {
        display: isColDisplayed("Address"),
        filter: false,
      },
    },
    {
      name: "PostalNr",
      label: "Postnr",
      options: {
        display: isColDisplayed("PostalNr"),
        filter: false,
      },
    },
    {
      name: "City",
      label: "Stad",
      options: {
        display: isColDisplayed("City"),
        filter: false,
      },
    },
    {
      Name: "Betalningsdatum",
      label: "PaymentDate",
      options: {
        display: isColDisplayed("City"),
        filter: false,
      },
    },
    {
      name: "Comment",
      label: "Kommentar",
      options: {
        display: isColDisplayed("Comment"),
        filter: false,
      },
    },
    {
      name: "FileId",
      label: "Fil ID",
      options: {
        display: isColDisplayed("FileId"),
        customBodyRender: (value) => {
          return <Link to={`/files/${value}`}>{value}</Link>;
        },
      },
    },
    {
      name: "AccountRefId",
      label: "Slutkundskonto ID",
      options: {
        display: isColDisplayed("AccountRefId"),
        filter: false,
        customBodyRender: (value) => {
          return <Link to={`/endcustomersaccounts/${value}`}>{value}</Link>;
        },
      },
    },
    {
      name: "InvoiceId",
      label: "Faktura ID",
      options: {
        display: isColDisplayed("InvoiceId"),
        customBodyRender: (value) => {
          return <Link to={`/invoices/${value}`}>{value}</Link>;
        },
      },
    },
    {
      name: "Kontrollstatus",
      options: {
        display: isColDisplayed("Kontrollstatus"),
        customBodyRender: (value) => {
          if (isColDisplayed("Kontrollstatus")) {
            return (
              <CommonChip
                labelName={value}
                color={getParseStatusColorByLabel(value)}
              />
            );
          }
        },
      },
    },
    {
      name: "ParseMessage",
      label: "Kontrollmedelande",
      options: {
        display: isColDisplayed("ParseMessage"),
      },
    },
    {
      name: "Status",
      options: {
        filter: true,
        sort: true,
        display: isColDisplayed("Status"),
        customBodyRender: (value) => {
          return (
            <CommonChip
              labelName={value}
              color={getSystemStatusColorByLabel(value)}
            />
          );
        },
      },
    },
  ];

  const options = {
    rowsPerPage: 100,
    rowsPerPageOptions: [10, 30, 100],
    selectableRowsHeader: true,
    selectableRowsHideCheckboxes: true,
    tableBodyMaxHeight: "70vh",
    onViewColumnsChange: (changedColumn, action) => {
      handleColumnShow(changedColumn, action);
    },
    downloadOptions: {
      separator: ";",
      filename: "all_clients.csv",
    },
    onDownload: (buildHead, buildBody, columns, data) => {
      return "\uFEFF" + buildHead(columns) + buildBody(data);
    },
  };

  return <MUITable options={options} data={rows} columns={columns} />;
}
